//External libs
import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse} from '@angular/common/http';
import { Subscription } from 'rxjs';

//Local libs
import Service from 'src/app/common/service';
import BarCodeModel from './barCode.model';
import BarcodeModel from './barCode.model';

/**
 * @service BarCodeService
 * @description Service for managing barcode data operations. Provides HTTP methods for creating
 * and retrieving barcodes from the backend API. Extends base Service class with generic type BarCodeModel.
 *
 * @feature 1. Fetch barcodes by business ID (filtered query)
 * @feature 2. Create/add new barcode records
 * @feature 3. Cancel ongoing HTTP requests to prevent memory leaks
 * @feature 4. Promise-based async operations for barcode management
 *
 * @architecture
 * Extends base Service<BarCodeModel> providing REST API integration with configurable base URL
 * and service endpoint. Implements request tracking for cleanup and cancellation. Uses HttpClient
 * for HTTP operations with standard error handling.
 *
 * @service-endpoint /aqu/barcode/
 * @service-provider root (singleton, application-wide)
 *
 * @author AquaCloud Development Team
 * @version 1.0.0
 * @created 2025-01-19
 */
@Injectable({
  providedIn: 'root'
})
export class BarCodeService extends Service<BarCodeModel> {

  /**
   * @type {Array<Subscription>}
   * @description Array tracking all ongoing HTTP request subscriptions
   * @default []
   * @side-effect Used to store subscription references for cancellation via cancelOnGoingHttpRequests()
   */
  public ongoingRequests: Subscription[] = [];

  /**
   * @method constructor
   * @description Service constructor with dependency injection of HttpClient
   *
   * Initializes service by calling parent Service constructor with baseUrl
   * configuration and service endpoint path.
   *
   * @param {HttpClient} httpClient - @service Angular HTTP client for making HTTP requests
   * @returns {void}
   */
  constructor(public httpClient: HttpClient) {
    super(httpClient, '/aqu/barcode/');
  }

  /**
   * @method getBarcodeByBusinessId
   * @async
   * @description Fetch all barcode records associated with a specific business ID
   *
   * Makes HTTP GET request to backend endpoint /aqu/barcode/businessId/{businessId}
   * to retrieve all barcodes for the given business. Wraps response in Promise for
   * async/await compatibility.
   *
   * @param {string} businessId - Business ID to filter barcodes by
   * @returns {Promise<Array<BarCodeModel>>} Promise resolving to array of BarCodeModel objects
   * @side-effect
   * - Creates HTTP GET request and stores subscription reference in ongoingRequests
   * - Subscription can be cancelled via cancelOnGoingHttpRequests()
   *
   * @throws Rejects promise with HttpErrorResponse if request fails
   * @example
   * const barcodes = await barcodeService.getBarcodeByBusinessId('123');
   * console.log(barcodes); // Array of BarCodeModel objects
   */
  public getBarcodeByBusinessId(businessId: string) {
    return new Promise<Array<BarCodeModel>>((resolve, reject) => {
      const request = this.httpClient.get<Array<BarCodeModel>>(`${this.baseUrl}${this.serviceUrl}businessId/${businessId}`, this.httpOptions).subscribe(response => {
        resolve(response);
      }, (error: HttpErrorResponse) => {
        reject(error);
      });
      this.ongoingRequests.push(request);
    });
  }

  /**
   * @method addBarcode
   * @async
   * @description Create a new barcode record in the database
   *
   * Makes HTTP POST request to backend endpoint /aqu/barcode/ with barcode data
   * to create new barcode record. Wraps response in Promise for async/await compatibility.
   *
   * @param {BarCodeModel} barcode - Barcode object containing all required fields for creation
   * @returns {Promise<BarCodeModel>} Promise resolving to created BarCodeModel with ID and metadata
   * @side-effect
   * - Creates HTTP POST request and stores subscription reference in ongoingRequests
   * - Subscription can be cancelled via cancelOnGoingHttpRequests()
   *
   * @throws Rejects promise with HttpErrorResponse if request fails or validation fails
   * @example
   * const newBarcode = new BarCodeModel();
   * newBarcode.code = 'BC123456';
   * const result = await barcodeService.addBarcode(newBarcode);
   * console.log(result.id); // Newly created barcode ID
   */
  public addBarcode(barcode: BarCodeModel) {
    return new Promise<BarCodeModel>((resolve, reject) => {
      const request = this.httpClient.post<BarCodeModel>(`${this.baseUrl}${this.serviceUrl}`, barcode, this.httpOptions).subscribe(response => {
        resolve(response);
      }, (error: HttpErrorResponse) => {
        reject(error);
      });
      this.ongoingRequests.push(request);
    });
  }

  /**
   * Validates a barcode for a given business ID.
   *
   * @method
   * @returns {Promise<BarcodeModel>} A promise resolving with the BarcodeModel object for validation.
   * @throws {HttpErrorResponse} Throws an HTTP error if the request fails.
   *
   * Description:
   * Typically called in {@link AddtankComponent}, {@link BatchTankComponent}, {@link BatchTankUcComponent}, 
   * and {@link TanksComponent}.
   */
  public getBarcodeValidation(barCode: string): Promise<BarcodeModel> {
    return new Promise<BarcodeModel>((resolve, reject) => {
      const request = this.httpClient.get<BarcodeModel>(`${this.baseUrl}${this.serviceUrl}businessId//barcode/${barCode}`, this.httpOptions).subscribe(
        response => resolve(response),
        (error: HttpErrorResponse) => reject(error)
      );
      this.ongoingRequests.push(request);
    });
  }

  /**
   * @method cancelOnGoingHttpRequests
   * @description Cancel all ongoing/pending HTTP requests to prevent memory leaks
   *
   * Iterates through ongoingRequests array and unsubscribes from each active
   * subscription. Clears the requests array after unsubscribing from all.
   *
   * @execution-flow
   * 1. Iterate through each subscription in ongoingRequests array
   * 2. Check if subscription has unsubscribe function
   * 3. Call unsubscribe() on each active subscription
   * 4. Clear ongoingRequests array
   *
   * @returns {void}
   * @side-effect
   * - Aborts all pending HTTP requests
   * - Clears ongoingRequests array
   * - Prevents completion callbacks for cancelled requests
   *
   * @usage Called during component ngOnDestroy to cleanup subscriptions
   * @example
   * ngOnDestroy() {
   *   this.barcodeService.cancelOnGoingHttpRequests();
   * }
   */
  public cancelOnGoingHttpRequests(): void {
    this.ongoingRequests.forEach((request) => {
      if (request && typeof request.unsubscribe === 'function') {
        request.unsubscribe();
      }
    });
    this.ongoingRequests = [];
  }
}
